import { CheckCircle2, Loader2, WifiOff } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useNetworkStatus } from '../../shell/SocketProvider';
import styles from './OfflineBanner.module.css';

// SYS-06. Driven by the SOCKET's connection state, not just `navigator.onLine`
// (see useNetworkStatus for why). Sits above the utility bar and pushes the layout
// down rather than floating over it.

/** How long the "back online" confirmation stays before removing itself. */
const RESTORED_MS = 3_000;

export function OfflineBanner(): ReactNode {
  const status = useNetworkStatus();
  const [previousStatus, setPreviousStatus] = useState(status);
  const [isShowingRestored, setIsShowingRestored] = useState(false);

  // Adjusting state during render rather than in an effect — React's documented
  // pattern for "a prop changed, derive from it". An effect here would render
  // once with the stale value and then again with the right one.
  if (status !== previousStatus) {
    setPreviousStatus(status);
    // Only confirm recovery if there was something to recover from; otherwise
    // every page load would flash "back online" at someone who never went away.
    setIsShowingRestored(status === 'online' && previousStatus !== 'online');
  }

  useEffect(() => {
    if (!isShowingRestored) return;
    const timer = window.setTimeout(() => setIsShowingRestored(false), RESTORED_MS);
    return () => window.clearTimeout(timer);
  }, [isShowingRestored]);

  if (status === 'offline') {
    return (
      <div className={`${styles.banner} ${styles.offline}`} role="alert">
        <span className={styles.icon} aria-hidden>
          <WifiOff size={16} />
        </span>
        <span className={styles.message}>
          You are offline. Changes you make now will not be saved until the connection returns.
        </span>
      </div>
    );
  }

  if (status === 'reconnecting') {
    return (
      <div className={`${styles.banner} ${styles.reconnecting}`} role="status">
        <span className={styles.spinner} aria-hidden>
          <Loader2 size={16} />
        </span>
        <span className={styles.message}>
          Reconnecting. What is on screen may be out of date.
        </span>
      </div>
    );
  }

  if (isShowingRestored) {
    return (
      <div className={`${styles.banner} ${styles.restored}`} role="status">
        <span className={styles.icon} aria-hidden>
          <CheckCircle2 size={16} />
        </span>
        <span className={styles.message}>Back online. This screen has caught up.</span>
      </div>
    );
  }

  return null;
}
